import { useMemo, useState } from 'react';
import { Box, Typography, Card, CardContent, Button, TextField, Tabs, Tab } from '@mui/material';
import { matchByJerseyNumber, matchByName } from '../../utils/athleteLookup';
import { derivePersonalBests } from '../../utils/testStats';
import { formatPositions } from '../../utils/footballPositions';
import { unitLabel } from '../../utils/testUnits';
import TestAttemptCapture from '../athletes/TestAttemptCapture';
import TeamTestLeaderboard from './TeamTestLeaderboard';
import CoachTestManagerDialog from './CoachTestManager';

const MEASUREMENT_METHOD_LABEL = { manual: 'Own Stopwatch', app_stopwatch: 'FFN Stopwatch' };

// Testing Occurrence / Attempt Protocol (059) — the Team-context
// structured reason lists, matching migration 059's own CHECK
// constraint exactly (chk_test_occurrence_actions_reason_shape).
// Personal Testing never sees either list (PersonalTestingStation.js
// passes no reasonOptions at all for End Test, and never exposes
// Reopen in V1).
const END_TEST_REASON_OPTIONS = [
  { value: 'pr_achieved', label: 'PR Achieved' },
  { value: 'quality_dropoff', label: 'Quality Drop-off' },
  { value: 'fatigue', label: 'Fatigue' },
  { value: 'discomfort_or_injury', label: 'Discomfort / Injury Concern' },
  { value: 'coach_discretion', label: 'Coach Discretion' },
  { value: 'other', label: 'Other' },
];
const REOPEN_REASON_OPTIONS = [
  { value: 'timing_or_equipment_issue', label: 'Timing / Equipment Issue' },
  { value: 'technique_correction', label: 'Technique Correction' },
  { value: 'coach_discretion', label: 'Coach Discretion' },
  { value: 'other', label: 'Other' },
];

// Testing Occurrence / Attempt Protocol (059) — one cache key per
// (athlete, test) pairing, exactly mirroring the grouping test_
// occurrences itself uses server-side (unique(session_id, test_type)) —
// scoped by athlete_id/test key here since the Station has no
// session_id to key by until the first attempt of a visit returns one.
function occurrenceKey(athleteId, testTypeKey) {
  return `${athleteId}:${testTypeKey}`;
}

// Phase C.7a — MANUAL TEAM TESTING STATION, extended in C.7a.1 with a
// per-station MEASUREMENT METHOD choice for timed tests.
//
// Team → Testing → select test → [timed tests only: "How will this data
// be measured?"] → jersey/name lookup → resolve candidate(s) →
// explicit athlete confirmation → ARMED → result capture (manual entry,
// or FFN Stopwatch START/STOP) → RECORD → PB feedback → back to lookup,
// with the SAME test AND measurement method still selected (neither
// resets between athletes — that's the entire point of a station).
//
// Deliberately no react-router-dom import — this component is pure
// props-in/callbacks-out (team, roster, testTypes, fetchAthleteSessions,
// recordAttempt) so it can be unit-tested directly with RTL, and so the
// data-fetching/routing concerns stay in the thin page wrapper
// (TeamTestingPage.js).
//
// MEASUREMENT METHOD (C.7a.1) is a STATION setting, not an athlete-by-
// athlete question — asked once per test (measurementMethodByTestKey is
// keyed by test, not reset when switching tests and back), never
// changeable mid-attempt (the "Change" action only renders in 'lookup'
// mode). Only tests with test_types.supports_app_stopwatch = true (056)
// ever ask — every other test (Standing Broad Jump, Vertical Jump, and
// any future test authored without stopwatch support) keeps the exact
// existing manual-entry flow untouched, with no stopwatch concept
// invented for it.
//
// STATE MACHINE — for this manual, single-device phase, several
// conceptual states deliberately collapse into fewer UI states:
//   WAITING            -> mode 'lookup'
//   ATHLETE IDENTIFIED -> mode 'lookup' (candidates shown, none picked yet)
//   IDENTITY CONFIRMED -> mode 'confirm'
//   ARMED              -> mode 'armed', stopwatchPhase 'ready' (FFN
//                         Stopwatch) or no stopwatch phase at all (Own
//                         Stopwatch / non-timed — straight to manual entry)
//   ATTEMPT IN PROGRESS -> stopwatchPhase 'running' (FFN Stopwatch only;
//                         has no equivalent for manual entry)
//   RESULT CAPTURED    -> stopwatchPhase 'captured' (FFN Stopwatch — a
//                         CANDIDATE value, not yet persisted) or the
//                         manual result field simply being filled in
//   CONFIRMED          -> written via recordAttempt(), then mode
//                         'feedback' -> "Next Athlete" returns to 'lookup'
//   CANCEL             -> back to 'lookup', nothing persisted
//   RETRY (FFN Stopwatch only) -> discards the candidate elapsed value,
//                         same armed athlete returns to stopwatchPhase 'ready'
// ALL of this is TRANSIENT React state — nothing here is written to the
// database until RECORD succeeds. A page refresh loses it entirely, by
// design (see teamTestingService.js's own header for why that's correct
// for this phase). The elapsed time itself comes ONLY from
// performance.now() (monotonic, high-resolution) — never Date.now()
// subtraction, and never any second device's clock.
export default function TeamTestingStation({
  team, roster, testTypes, fetchAthleteSessions, recordAttempt,
  fetchSessionLeaderboard, fetchSeasonLeaderboard,
  // Coach-Owned Test Types (058) — all optional/backward-compatible: a
  // caller that omits them (any existing test/usage) simply never
  // renders the "My Tests" action, exactly as before this phase.
  // canManageTests is the CONTEXTUAL gate (is the viewer a Head Coach of
  // THIS team) — deliberately narrower than create_coach_test_type()'s
  // own server-side floor (any active head_coach role, anywhere); the
  // RPC remains the actual authority regardless of this affordance.
  canManageTests, myTests, onCreateTest, onUpdateTest,
  // Testing Occurrence / Attempt Protocol (059) — all optional/backward-
  // compatible, same discipline as canManageTests above: a caller that
  // omits them (any existing test) simply never renders the Change Max/
  // End Test/Reopen affordances, and the capture flow behaves exactly
  // as it always has. Authorization for all three is re-derived
  // server-side (059) using the EXACT SAME tier record_team_test_
  // attempt() itself already requires — anyone who can reach this
  // Station at all (TeamTestingPage.js only renders it when
  // canConductTesting is true) already has that tier, so no additional
  // authorization prop is needed here to gate these controls.
  onEndTestEarly, onChangeOccurrenceMax, onReopenOccurrence,
}) {
  const [testTypeKey, setTestTypeKey] = useState(testTypes[0]?.key);
  // Coach-Owned Test Types (058) — "My Tests" manager dialog. Deliberately
  // a single boolean, not woven into the capture-flow `mode` state
  // machine above: opening it never interrupts an in-progress lookup/
  // confirm/armed attempt, and closing it never resets one either.
  const [manageOpen, setManageOpen] = useState(false);
  const [mode, setMode] = useState('lookup'); // 'lookup' | 'confirm' | 'armed' | 'feedback'
  const [jerseyQuery, setJerseyQuery] = useState('');
  const [nameQuery, setNameQuery] = useState('');
  const [pendingRow, setPendingRow] = useState(null); // roster row awaiting YES/NO
  const [armedRow, setArmedRow] = useState(null); // roster row locked in for this attempt
  const [previousPb, setPreviousPb] = useState(undefined); // undefined = not yet loaded
  const [resultValue, setResultValue] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // { value, isNewPb, unit } after a successful RECORD
  // Keyed by athlete_id — the ONE piece of continuity this phase keeps,
  // entirely in memory: if the same athlete returns later in this same
  // station visit, their next attempt lands in the SAME testing
  // occurrence instead of starting a new one. Lost on refresh, exactly
  // as intended.
  const [sessionIdByAthlete, setSessionIdByAthlete] = useState({});
  // Testing Occurrence / Attempt Protocol (059) — cached, server-
  // confirmed occurrence state per (athlete, test), keyed exactly like
  // sessionIdByAthlete above and with the same lifetime/reset rules:
  // in-memory only for this station visit, never persisted, never
  // re-fetched on load. Populated only once the FIRST attempt for that
  // pairing this visit returns a real occurrence_id — before that, an
  // athlete+test's entry is simply absent, and TestAttemptCapture
  // renders exactly as it always has (no occurrence UI at all). The
  // server (059) remains the sole authority regardless of what this
  // cache believes at any moment.
  const [occurrenceByKey, setOccurrenceByKey] = useState({});
  // C.7a.1 — per-test-key measurement method ('manual' | 'app_stopwatch'),
  // asked once per timed test, retained as athletes rotate through it.
  const [measurementMethodByTestKey, setMeasurementMethodByTestKey] = useState({});
  // C.7a.1 — FFN Stopwatch sub-state, only ever relevant while ARMED on
  // a timed test with app_stopwatch selected.
  const [stopwatchPhase, setStopwatchPhase] = useState('ready'); // 'ready' | 'running' | 'captured'
  const [stopwatchStartedAt, setStopwatchStartedAt] = useState(null); // performance.now() at START
  const [candidateResult, setCandidateResult] = useState(null); // elapsed seconds at STOP — NOT yet persisted
  // TEAM TESTING LEADERBOARDS — the smallest clean invalidation
  // mechanism for this component structure: bumped ONLY inside
  // submitAttempt()'s success path (never on STOP/RETRY/Cancel, which
  // never touch it), so TeamTestLeaderboard's own effect refetches
  // exactly once per successful RECORD, no page reload needed.
  const [leaderboardRefreshToken, setLeaderboardRefreshToken] = useState(0);
  // 054 PRE-APPLICATION REVIEW — ONE explicit station date, computed
  // once per render, used for BOTH the write path (submitAttempt below)
  // AND the SESSION leaderboard query — never two independently-computed
  // "today" values that merely happen to agree. Same computation
  // (`new Date().toISOString().slice(0, 10)`) this station has always
  // used for test_date; timezone handling itself is intentionally
  // unchanged/out of scope for this fix — only single-source-of-truth
  // consistency between write and read.
  const stationTestDate = new Date().toISOString().slice(0, 10);

  const activeTestType = testTypes.find(t => t.key === testTypeKey) || testTypes[0];
  // Custom Test Types (056) — supports_app_stopwatch is now an explicit,
  // independent fact about the test (test_types.supports_app_stopwatch),
  // never inferred from unit === 'seconds'. That heuristic was
  // accidentally correct for the seven original built-ins but wrong in
  // general: a seconds-based test can legitimately be manual-only, and a
  // non-seconds test can never have a stopwatch by definition. A
  // server-side CHECK (056) is the actual enforcement of that second
  // rule — this `&& unit === 'seconds'` is a redundant, defense-in-depth
  // mirror of it, same convention as this schema's own repeated
  // trigger-re-verifies-what-the-RPC-already-checked pattern, so a
  // malformed row can never render a stopwatch prompt client-side either.
  const supportsStopwatch = Boolean(activeTestType?.supports_app_stopwatch) && activeTestType?.unit === 'seconds';
  const currentMethod = supportsStopwatch ? measurementMethodByTestKey[activeTestType.key] : 'manual';
  const needsMethodSelection = supportsStopwatch && !currentMethod;
  const isAppStopwatch = supportsStopwatch && currentMethod === 'app_stopwatch';

  const jerseyCandidates = useMemo(() => matchByJerseyNumber(roster, jerseyQuery), [roster, jerseyQuery]);
  const nameCandidates = useMemo(() => matchByName(roster, nameQuery), [roster, nameQuery]);
  // Jersey lookup takes priority when both happen to have a value —
  // the operator is expected to use one or the other, not both at once.
  const candidates = jerseyQuery ? jerseyCandidates : nameCandidates;

  const resetToLookup = () => {
    setMode('lookup');
    setJerseyQuery('');
    setNameQuery('');
    setPendingRow(null);
    setArmedRow(null);
    setPreviousPb(undefined);
    setResultValue('');
    setError(null);
    setFeedback(null);
    setStopwatchPhase('ready');
    setStopwatchStartedAt(null);
    setCandidateResult(null);
  };

  const handleTestTypeChange = (key) => {
    setTestTypeKey(key);
    resetToLookup(); // a mid-flow test change always aborts the current attempt — never mixes tests on one armed athlete
  };

  const handleSelectMethod = (method) => {
    setMeasurementMethodByTestKey(prev => ({ ...prev, [activeTestType.key]: method }));
  };

  // Only ever reachable from 'lookup' mode (see the render below) — the
  // measurement method can never silently change mid-attempt.
  const handleChangeMethod = () => {
    setMeasurementMethodByTestKey(prev => {
      const next = { ...prev };
      delete next[activeTestType.key];
      return next;
    });
  };

  const handlePickCandidate = (row) => {
    setPendingRow(row);
    setMode('confirm');
    setError(null);
  };

  const handleConfirmNo = () => {
    // REJECT at confirmation — clears the resolution, returns to lookup.
    // Nothing was ever armed or written.
    setPendingRow(null);
    setMode('lookup');
  };

  const handleConfirmYes = async () => {
    // Locks athlete_id for this attempt — no other lookup can overwrite
    // it until submit/cancel/reset.
    setArmedRow(pendingRow);
    setMode('armed');
    setPendingRow(null);
    setPreviousPb(undefined);
    setStopwatchPhase('ready');
    setStopwatchStartedAt(null);
    setCandidateResult(null);
    try {
      const sessions = await fetchAthleteSessions(pendingRow.athlete.id);
      const pbs = derivePersonalBests(sessions, testTypes);
      const pb = pbs.find(p => p.testType.key === activeTestType.key);
      setPreviousPb(pb || null);
    } catch {
      // Previous-PB context is best-effort only — never blocks the
      // station from recording a new attempt if the read fails.
      setPreviousPb(null);
    }
  };

  const handleCancelArmed = () => {
    // CANCEL — a meaningful measurement never completed. Nothing
    // persisted; simply returns to lookup.
    resetToLookup();
  };

  // The ONE place that actually calls recordAttempt() — both the manual
  // path (own stopwatch / non-timed) and the FFN Stopwatch's own RECORD
  // action funnel through here, with only rawValue/captureMethod
  // differing. There is exactly one write path, never two.
  const submitAttempt = async (rawValue, captureMethod) => {
    setError(null);
    const parsed = Number(rawValue);
    if (rawValue === '' || rawValue === null || !Number.isFinite(parsed) || parsed <= 0) {
      setError('Enter a positive number.');
      return;
    }
    setSaving(true);
    try {
      const athleteId = armedRow.athlete.id;
      const result = await recordAttempt({
        teamId: team.id,
        athleteId,
        testType: activeTestType.key,
        resultValue: parsed,
        testDate: stationTestDate,
        sessionId: sessionIdByAthlete[athleteId],
        captureMethod,
      });
      setSessionIdByAthlete(prev => ({ ...prev, [athleteId]: result.session_id }));
      // Testing Occurrence / Attempt Protocol (059) — cache the
      // server's own occurrence state from this exact response; occurrence
      // fields are absent on a mocked/legacy recordAttempt response
      // (result.occurrence_id undefined), in which case this is a no-op
      // and the capture flow behaves exactly as it did before 059.
      if (result.occurrence_id) {
        setOccurrenceByKey(prev => ({
          ...prev,
          [occurrenceKey(athleteId, activeTestType.key)]: {
            occurrenceId: result.occurrence_id,
            status: result.occurrence_status,
            validAttemptCount: result.valid_attempt_count,
            plannedMaxAttempts: result.planned_max_attempts,
          },
        }));
      }
      setLeaderboardRefreshToken(t => t + 1); // successful persistence only — never on STOP/RETRY/Cancel

      const isNewPb = previousPb
        ? (activeTestType.better_direction === 'lower' ? parsed < previousPb.value : parsed > previousPb.value)
        : true; // no prior PB at all — the first attempt is trivially the new PB
      setFeedback({ value: parsed, isNewPb, unit: unitLabel(activeTestType.unit) });
      setMode('feedback');
    } catch (e) {
      setError(e.message || 'Could not record this attempt.');
    } finally {
      setSaving(false);
    }
  };

  const handleManualRecord = () => submitAttempt(resultValue, 'manual');

  // C.7a.1 — FFN Stopwatch. Elapsed time comes ONLY from performance.now()
  // (monotonic, high-resolution) — never Date.now() subtraction, and
  // there is no second device's clock involved in this manual phase at
  // all. STOP never persists anything; it only produces a CANDIDATE
  // value the operator must still explicitly RECORD or RETRY.
  const handleStopwatchStart = () => {
    setError(null);
    setStopwatchStartedAt(performance.now());
    setStopwatchPhase('running');
  };

  const handleStopwatchStop = () => {
    const elapsedSeconds = (performance.now() - stopwatchStartedAt) / 1000;
    setCandidateResult(Math.round(elapsedSeconds * 100) / 100);
    setStopwatchPhase('captured');
  };

  const handleStopwatchRetry = () => {
    // Discards the candidate elapsed value; the SAME armed athlete
    // returns to the ready/start state — never re-asks for identity.
    setCandidateResult(null);
    setStopwatchStartedAt(null);
    setStopwatchPhase('ready');
  };

  const handleStopwatchRecord = () => submitAttempt(candidateResult, 'app_stopwatch');

  // Testing Occurrence / Attempt Protocol (059) — the three occurrence-
  // management actions. Each calls the injected service function, then
  // refreshes ONLY the cache entry for the currently-armed athlete+test
  // from the RPC's own returned test_occurrences row — validAttemptCount
  // is preserved from the existing cache entry (none of these three
  // actions change how many attempts have actually been recorded).
  const updateOccurrenceCache = (patch) => {
    if (!armedRow) return;
    const key = occurrenceKey(armedRow.athlete.id, activeTestType.key);
    setOccurrenceByKey(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const handleChangeMax = async (occId, newMax) => {
    const updated = await onChangeOccurrenceMax({ occurrenceId: occId, newMax });
    updateOccurrenceCache({ plannedMaxAttempts: updated.planned_max_attempts, status: updated.status });
  };

  const handleEndTest = async (occId, { reasonCode, note }) => {
    const updated = await onEndTestEarly({ occurrenceId: occId, reasonCode, note });
    updateOccurrenceCache({ status: updated.status });
  };

  const handleReopen = async (occId, newMax, { reasonCode, note }) => {
    const updated = await onReopenOccurrence({ occurrenceId: occId, newMax, reasonCode, note });
    updateOccurrenceCache({ plannedMaxAttempts: updated.planned_max_attempts, status: updated.status });
  };

  if (!activeTestType) {
    return <Typography sx={{ color: '#6a8aa8' }}>No test types available.</Typography>;
  }

  const resultUnitLabel = unitLabel(activeTestType.unit);

  return (
    <Box>
      <Tabs
        value={activeTestType.key}
        onChange={(e, v) => handleTestTypeChange(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 2, borderBottom: '1px solid #1e2a3a', minHeight: 48,
          '& .MuiTab-root': { minHeight: 48, textTransform: 'none', fontWeight: 800, fontSize: '0.95rem', px: 2.5 } }}
      >
        {testTypes.map(t => <Tab key={t.key} value={t.key} label={t.display_name} />)}
      </Tabs>

      {/* Coach-Owned Test Types (058) — a single, nearby action rather
          than weaving creation into the tab bar itself (per this phase's
          own explicit UX-debt decision: AVAILABLE TESTS != CURRENTLY
          SELECTED TESTS is left for a future picker/pinning pass). Only
          rendered for a Head Coach of THIS team — canManageTests is the
          contextual UI gate; create_coach_test_type()'s own server-side
          authorization (any active head_coach role, anywhere) remains
          the actual authority regardless of this button's visibility. */}
      {canManageTests && (
        <Box sx={{ mb: 2 }}>
          <Button
            size="small" variant="outlined" color="primary"
            onClick={() => setManageOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            My Tests
          </Button>
        </Box>
      )}

      {/* C.7a.1 — measurement-method line in the Station header, only for
          timed tests that already have a method chosen. Change is only
          ever offered between athletes (mode 'lookup') — never mid-attempt. */}
      {supportsStopwatch && currentMethod && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Typography sx={{ color: '#9eb8d0', fontSize: '0.85rem' }}>
            Measurement: <strong>{MEASUREMENT_METHOD_LABEL[currentMethod]}</strong>
          </Typography>
          {mode === 'lookup' && (
            <Button size="small" onClick={handleChangeMethod} sx={{ textTransform: 'none', color: '#6a8aa8', minWidth: 0, p: 0 }}>
              Change
            </Button>
          )}
        </Box>
      )}

      {needsMethodSelection ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', mb: 3 }}>How will this data be measured?</Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              onClick={() => handleSelectMethod('app_stopwatch')} variant="contained" color="primary" size="large"
              sx={{ textTransform: 'none', fontWeight: 800, px: 4, py: 2, fontSize: '1.05rem' }}
            >
              USE FFN STOPWATCH
            </Button>
            <Button
              onClick={() => handleSelectMethod('manual')} variant="outlined" color="primary" size="large"
              sx={{ textTransform: 'none', fontWeight: 800, px: 4, py: 2, fontSize: '1.05rem' }}
            >
              I HAVE MY OWN STOPWATCH
            </Button>
          </Box>
        </Box>
      ) : (
        <>
          {mode === 'lookup' && (
            <Box>
              <Typography sx={{ fontWeight: 900, fontSize: '0.85rem', letterSpacing: '0.05em', color: '#6a8aa8', mb: 1.5 }}>
                {activeTestType.display_name.toUpperCase()} STATION
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <TextField
                  label="Jersey Number" type="number" size="medium" autoFocus
                  value={jerseyQuery}
                  onChange={e => { setJerseyQuery(e.target.value); setNameQuery(''); }}
                  inputProps={{ inputMode: 'numeric', style: { fontSize: '1.4rem', fontWeight: 800 } }}
                  sx={{ width: 200 }}
                />
                <TextField
                  label="Or search by name" size="medium"
                  value={nameQuery}
                  onChange={e => { setNameQuery(e.target.value); setJerseyQuery(''); }}
                  sx={{ flex: 1, minWidth: 200 }}
                />
              </Box>

              {(jerseyQuery || nameQuery) && (
                candidates.length === 0 ? (
                  <Typography sx={{ color: '#6a8aa8' }}>No matching athlete on this roster.</Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {candidates.length > 1 && (
                      <Typography sx={{ color: '#f5a623', fontWeight: 700, fontSize: '0.85rem' }}>
                        Multiple athletes match — select the correct one:
                      </Typography>
                    )}
                    {candidates.map(row => (
                      <Card
                        key={row.id}
                        onClick={() => handlePickCandidate(row)}
                        sx={{ cursor: 'pointer', backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a', '&:hover': { borderColor: '#2ea84b' } }}
                      >
                        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
                          <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
                            {row.jersey_number !== null && row.jersey_number !== undefined && `#${row.jersey_number} `}
                            {row.athlete?.first_name} {row.athlete?.last_name}
                          </Typography>
                          <Typography sx={{ color: '#6a8aa8', fontSize: '0.85rem' }}>
                            {row.season_grade ? `Grade ${row.season_grade}` : ''} {formatPositions(row) !== '—' ? `· ${formatPositions(row)}` : ''}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )
              )}
            </Box>
          )}

          {mode === 'confirm' && pendingRow && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ color: '#6a8aa8', fontSize: '1rem', mb: 1 }}>This attempt is for</Typography>
              <Typography sx={{ fontWeight: 900, fontSize: '2.2rem', mb: 0.5 }}>
                {pendingRow.jersey_number !== null && pendingRow.jersey_number !== undefined && `#${pendingRow.jersey_number} `}
                {pendingRow.athlete?.first_name} {pendingRow.athlete?.last_name}
              </Typography>
              <Typography sx={{ color: '#9eb8d0', mb: 4 }}>Correct?</Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  onClick={handleConfirmNo} variant="outlined" color="secondary" size="large"
                  sx={{ textTransform: 'none', fontWeight: 800, px: 5, py: 1.5, fontSize: '1.1rem' }}
                >
                  NO
                </Button>
                <Button
                  onClick={handleConfirmYes} variant="contained" color="primary" size="large"
                  sx={{ textTransform: 'none', fontWeight: 800, px: 5, py: 1.5, fontSize: '1.1rem' }}
                >
                  YES
                </Button>
              </Box>
            </Box>
          )}

          {mode === 'armed' && armedRow && (
            <TestAttemptCapture
              phase="armed"
              subject={{ jerseyNumber: armedRow.jersey_number, firstName: armedRow.athlete?.first_name, lastName: armedRow.athlete?.last_name }}
              previousPb={previousPb}
              unitLabel={resultUnitLabel}
              isAppStopwatch={isAppStopwatch}
              stopwatchPhase={stopwatchPhase}
              stopwatchStartedAt={stopwatchStartedAt}
              candidateResult={candidateResult}
              resultValue={resultValue}
              onResultChange={setResultValue}
              error={error}
              saving={saving}
              onStopwatchStart={handleStopwatchStart}
              onStopwatchStop={handleStopwatchStop}
              onStopwatchRetry={handleStopwatchRetry}
              onStopwatchRecord={handleStopwatchRecord}
              onManualRecord={handleManualRecord}
              onCancel={handleCancelArmed}
              occurrence={occurrenceByKey[occurrenceKey(armedRow.athlete.id, activeTestType.key)]}
              onEndTest={onEndTestEarly ? handleEndTest : undefined}
              endTestReasonOptions={END_TEST_REASON_OPTIONS}
              onChangeMax={onChangeOccurrenceMax ? handleChangeMax : undefined}
              onReopen={onReopenOccurrence ? handleReopen : undefined}
              reopenReasonOptions={REOPEN_REASON_OPTIONS}
            />
          )}

          {mode === 'feedback' && feedback && armedRow && (
            // BUGFIX audit (059) — "Next Athlete" is deliberately NEVER
            // hidden here, even when this athlete's occurrence just
            // completed: unlike Personal's "Test Again" (a literal
            // capture-continuation for THIS SAME occurrence, genuinely
            // contradictory once complete), "Next Athlete" only clears
            // the armed athlete and returns to roster lookup — always a
            // valid action regardless of Liam's own test being done, and
            // the coach's only way off this screen to test the next kid.
            // hideNextWhenCompleted is intentionally never passed here.
            // Re-arming the SAME athlete afterward correctly lands back
            // on TestAttemptCapture's own ARMED-phase "TEST COMPLETE"
            // branch (occurrence, onReopen, reopenReasonOptions below),
            // which is the actual, only place Reopen is offered.
            <TestAttemptCapture
              phase="feedback"
              subject={{ jerseyNumber: armedRow.jersey_number, firstName: armedRow.athlete?.first_name, lastName: armedRow.athlete?.last_name }}
              previousPb={previousPb}
              feedback={feedback}
              nextActionLabel="Next Athlete"
              onNext={resetToLookup}
              occurrence={occurrenceByKey[occurrenceKey(armedRow.athlete.id, activeTestType.key)]}
              onEndTest={onEndTestEarly ? handleEndTest : undefined}
              endTestReasonOptions={END_TEST_REASON_OPTIONS}
            />
          )}
        </>
      )}

      {/* TEAM TESTING LEADERBOARDS — a companion panel, not a second
          test selector: always scoped to whichever test tab is
          currently active above, regardless of lookup/confirm/armed/
          feedback mode, so a coach can glance at standings without the
          capture workflow being interrupted. Never rendered for
          Personal Testing (no team context exists there). */}
      {fetchSessionLeaderboard && fetchSeasonLeaderboard && (
        <TeamTestLeaderboard
          testType={activeTestType}
          testDate={stationTestDate}
          fetchSessionLeaderboard={fetchSessionLeaderboard}
          fetchSeasonLeaderboard={fetchSeasonLeaderboard}
          refreshToken={leaderboardRefreshToken}
        />
      )}

      {canManageTests && (
        <CoachTestManagerDialog
          open={manageOpen}
          myTests={myTests}
          onClose={() => setManageOpen(false)}
          onCreate={onCreateTest}
          onUpdate={onUpdateTest}
        />
      )}
    </Box>
  );
}
